import {faPencil, faUpload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ActionIcon, Box, Button, Group, Text, Tooltip} from '@mantine/core';
import {Dispatch, FC, SetStateAction, useMemo} from 'react';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {DocumentsService} from '../../api/generated/DocumentsService';
import {
  PagedDataTableConfig,
  usePagedDataTable,
} from '../../hooks/paged-data-table/use-paged-data-table';
import {ROUTES} from '../../routes';
import {useJobStore} from '../job-store';
import {renderedDateFormat} from '../../utils/luxon.ts';
import {useAsync} from 'react-use';
import {AnchorIcon} from '../../components/anchor-icon.tsx';
import {faTrash} from '@fortawesome/pro-solid-svg-icons';
import {onClickDelete, onConfirmDelete} from './on-delete-actions.ts';
import {JobDetailDto} from '../../api/generated/index.defs.ts';

type ReviewDocumentListingProps = {
  setCreateOpen: Dispatch<SetStateAction<boolean>>;
  setUpdateOpen: Dispatch<SetStateAction<boolean>>;
  setDocumentId: Dispatch<SetStateAction<number | undefined>>;
};

export const ReviewDocumentListing: FC<ReviewDocumentListingProps> = ({
  setCreateOpen,
  setUpdateOpen,
  setDocumentId,
}) => {
  const {id} = useTypedParams(ROUTES.ADMIN.JOB_REVIEWS.DETAIL);

  const {job, isJobReadonly, fetchJob} = useJobStore((state) => ({
    job: state.job,
    isJobReadonly: state.isJobReadonly,
    fetchJob: state.fetchJob,
  }));

  useAsync(async () => {
    if (!job) {
      await fetchJob(id);
    }
  }, [fetchJob, id, job]);

  const tableConfig = useMemo(
    () =>
      TableConfig({setDocumentId, setUpdateOpen, job, isJobReadonly, fetchJob}),
    [fetchJob, isJobReadonly, job, setDocumentId, setUpdateOpen],
  );

  const pagedDataTable = usePagedDataTable(
    DocumentsService.getAllByReviewId,
    tableConfig,
    {
      additionalParams: {
        id,
      },
      renderActions: (
        <>
          {!isJobReadonly && (
            <Button onClick={() => setCreateOpen(true)}>
              <Group gap='xs'>
                <FontAwesomeIcon icon={faUpload} /> Upload File
              </Group>
            </Button>
          )}
        </>
      ),
    },
  );

  return <Box>{pagedDataTable}</Box>;
};

type TableConfigProps = {
  setDocumentId: Dispatch<SetStateAction<number | undefined>>;
  setUpdateOpen: Dispatch<SetStateAction<boolean>>;
  job: JobDetailDto | null;
  isJobReadonly?: boolean;
  fetchJob: (jobId: number) => Promise<void>;
};

const TableConfig = ({
  setDocumentId,
  setUpdateOpen,
  job,
  isJobReadonly,
  fetchJob,
}: TableConfigProps) => {
  return PagedDataTableConfig(DocumentsService.getAllByReviewId, {
    columns: [
      {
        title: '',
        accessor: '',
        render: (item) => (
          <Tooltip label={'Edit Document'}>
            <ActionIcon
              disabled={item.isReadonly || isJobReadonly}
              className='clear'
              onClick={() => {
                setDocumentId(item.id);
                setUpdateOpen(true);
              }}
              aria-label={'Edit Document'}
            >
              <FontAwesomeIcon icon={faPencil} />
            </ActionIcon>
          </Tooltip>
        ),
        width: 40,
      },
      {
        title: '',
        accessor: '',
        render: (item) => (
          <Tooltip label='Delete Document'>
            <ActionIcon
              disabled={item.isReadonly || isJobReadonly}
              className='clear'
              aria-label='Delete Document'
              onClick={() =>
                onClickDelete({
                  title: <Text fw={600}>Are you sure?</Text>,
                  onConfirm: async () =>
                    await onConfirmDelete(Number(item.id), job?.id, fetchJob),
                  labels: {cancel: 'Cancel', confirm: 'Delete'},
                })
              }
            >
              <FontAwesomeIcon icon={faTrash} />
            </ActionIcon>
          </Tooltip>
        ),
        width: 80,
      },
      {
        title: 'Document ID',
        accessor: 'id',
      },
      {
        accessor: 'name',
        render: (item) => (
          <AnchorIcon href={item.url} target='_blank' rel='noopener noreferrer'>
            {item.name}
          </AnchorIcon>
        ),
        sortable: true,
      },
      {
        accessor: 'codeDescription',
        title: 'Type',
        sortable: true,
      },
      {
        accessor: 'createdByUserLastName',
        title: 'Entered By',
        render: (item) =>
          `${item.createdByUserFirstName} ${item.createdByUserLastName}`,
        sortable: true,
      },
      {
        title: 'Date',
        accessor: 'createdDate',
        render: (item) => renderedDateFormat(item.createdDate),
        sortable: true,
      },
    ],
    searchFieldNames: ['name', 'codeDescription'],
    defaultSort: {
      column: 'createdDate',
      direction: 'DESC',
    },
    refetchEvent: 'document-listing',
  });
};
